import { TeamsFile } from "@/util/interfaces/TeamsFile";

// Base type for tree nodes
export interface BaseTreeNode {
  id: string;
  name: string;
  folder: boolean;
  category: ("Deloitte data" | "Client data" | "Third-party data")[] | null;
  level: number;
}

// Generic type for trees with children
export interface TreeNodeWithChildren<T extends BaseTreeNode> extends BaseTreeNode {
  children: T[];
}

// Helper function to add descendants
const addDescendants = (
  parentId: string,
  requiredIds: Set<string>,
  getTeamsFilesByParentId: (parentId: string) => TeamsFile[]
) => {
  const children = getTeamsFilesByParentId(parentId);
  for (const child of children) {
    requiredIds.add(child.id);
    if (child.folder) {
      addDescendants(child.id, requiredIds, getTeamsFilesByParentId);
    }
  }
};

// Function to get the ancestor level of a file
const getAncestorLevel = (
  file: TeamsFile,
  requiredIds: Set<string>,
  getTeamsFileById: (id: string) => TeamsFile | undefined
): number => {
  let level = 0;
  let currentParentId: string | undefined = file.parentReference?.id;
  while (currentParentId && requiredIds.has(currentParentId)) {
    level++;
    const parentFile = getTeamsFileById(currentParentId);
    currentParentId = parentFile?.parentReference?.id;
  }
  return level;
};

// Main function to build the tree
export const buildTreeFromSelectedFiles = <T extends TreeNodeWithChildren<T>>(
  selectedFiles: TeamsFile[],
  getTeamsFilesByParentId: (parentId: string) => TeamsFile[],
  getTeamsFileById: (id: string) => TeamsFile | undefined,
  nodeMapper: (file: TeamsFile, level: number) => Omit<T, "children">
): T[] => {
  if (!selectedFiles || selectedFiles.length === 0) return [];

  // Set of all IDs that should be in the tree
  const requiredIds = new Set<string>();

  // Add all files from selectedRows
  for (const file of selectedFiles) {
    requiredIds.add(file.id);

    // SCENARIO 1: If it's a selected folder, add all its descendants
    if (file.folder) {
      addDescendants(file.id, requiredIds, getTeamsFilesByParentId);
    }

    // SCENARIO 2: Add all ancestors to maintain hierarchy
    let currentParentId: string | undefined = file.parentReference?.id;
    while (currentParentId) {
      if (requiredIds.has(currentParentId)) {
        break;
      } else {
        requiredIds.add(currentParentId);
        const parentFile = getTeamsFileById(currentParentId);
        currentParentId = parentFile?.parentReference?.id;
      }
    }
  }

  // Get all required files
  const requiredFiles = Array.from(requiredIds)
    .map((id) => getTeamsFileById(id))
    .filter((file): file is TeamsFile => file !== undefined);

  // Recursive function to build the tree
  const buildTree = (parentId: string | null, level: number = 0): T[] => {
    const children = requiredFiles.filter((file) => file.parentReference?.id === parentId);

    return children.map((file) => ({
      ...nodeMapper(file, level),
      children: buildTree(file.id, level + 1),
    })) as T[];
  };

  // Find the MINIMUM ancestor level (closest to root)
  const minLevel = Math.min(
    ...requiredFiles.map((file) => getAncestorLevel(file, requiredIds, getTeamsFileById))
  );

  // Root files are those with the MINIMUM ancestor level
  const rootFiles = requiredFiles.filter(
    (file) => getAncestorLevel(file, requiredIds, getTeamsFileById) === minLevel
  );

  const tree = rootFiles.map((file) => ({
    ...nodeMapper(file, 0),
    children: buildTree(file.id, 1),
  })) as T[];

  return tree;
};
